import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { whatsappAgentAPI } from '../../../../helpers/whatsappApiHandler';
import AddEditContactModal from '../modals/AddEditContactModal';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import EmptyState from './EmptyState';
import MessageInput from './MessageInput';

// Add pulse and spin animation keyframes
const animationKeyframes = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject keyframes into document head
if (typeof document !== 'undefined' && !document.getElementById('whatsapp-chat-keyframes')) {
  const style = document.createElement('style');
  style.id = 'whatsapp-chat-keyframes';
  style.textContent = animationKeyframes;
  document.head.appendChild(style);
}

// Utility function to transform API message format to UI format
const transformMessageForUI = (apiMessage) => {
  // Validate required fields
  if (!apiMessage || typeof apiMessage !== 'object') {
    throw new Error('Invalid message object');
  }

  if (!apiMessage.id) {
    throw new Error('Message missing required id field');
  }

  if (!apiMessage.sent_at) {
    throw new Error('Message missing required sent_at field');
  }

  const isOutgoing = apiMessage.direction === 'outbound';
  let timestamp;

  try {
    timestamp = new Date(apiMessage.sent_at);
    if (isNaN(timestamp.getTime())) {
      throw new Error('Invalid timestamp');
    }
  } catch (error) {
    console.warn('Invalid sent_at timestamp, using current time:', apiMessage.sent_at);
    timestamp = new Date();
  }

  // Format timestamp for display
  const formatTime = (date) => {
    try {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return 'Invalid time';
    }
  };

  // Get message text - prefer direct text field, fallback to content.text, then message_text
  const messageText =
    apiMessage.text || apiMessage.content?.text || apiMessage.message_text || 'No message content';

  // Get sender/agent name for outbound messages and internal notes
  const getAgentName = () => {
    // For internal notes, get agent name from content.agent_context
    if (apiMessage.direction === 'internal' && apiMessage.content?.agent_context?.from_agent_name) {
      return apiMessage.content.agent_context.from_agent_name;
    }

    // For regular outbound messages, check metadata first, then fallback to content
    if (isOutgoing) {
      // Check metadata for agent information (new format)
      if (apiMessage.metadata?.agent?.name) {
        return apiMessage.metadata.agent.name;
      }

      // Fallback to content sender_name (legacy format)
      return apiMessage.content?.sender_name || 'Agent';
    }

    return null;
  };

  const agentName = getAgentName();

  // Determine chat message type based on message properties
  const getChatMessageType = () => {
    // Check if it's an internal note - backend sends direction: "internal" and message_type: "note"
    if (apiMessage.direction === 'internal' && apiMessage.message_type === 'note') {
      return 'internal_note';
    }

    // Also check for legacy internal note formats
    if (
      apiMessage.message_type === 'internal_note' ||
      apiMessage.type === 'internal_note' ||
      apiMessage.is_internal_note === true ||
      (apiMessage.content && apiMessage.content.type === 'internal_note') ||
      (apiMessage.content && apiMessage.content.note_type === 'internal_note')
    ) {
      return 'internal_note';
    }

    // Check direction for regular messages
    if (isOutgoing) {
      return 'outbound';
    } else {
      return 'inbound';
    }
  };

  return {
    id: apiMessage.id,
    text: messageText,
    timestamp: formatTime(timestamp),
    fullTimestamp: timestamp,
    isOutgoing,
    agentName,
    messageType: apiMessage.message_type || 'text',
    chatMessageType: getChatMessageType(), // New field for ChatMessage component
    status: apiMessage.status || 'unknown',
    messageId: apiMessage.message_id,
  };
};

// Utility function to group messages by date
const groupMessagesByDate = (messages) => {
  const groups = [];
  let currentDate = null;
  let currentGroup = [];

  messages.forEach((message) => {
    const messageDate = message.fullTimestamp.toDateString();

    if (messageDate !== currentDate) {
      if (currentGroup.length > 0) {
        groups.push({
          date: currentDate,
          messages: currentGroup,
        });
      }
      currentDate = messageDate;
      currentGroup = [message];
    } else {
      currentGroup.push(message);
    }
  });

  if (currentGroup.length > 0) {
    groups.push({
      date: currentDate,
      messages: currentGroup,
    });
  }

  return groups;
};

const WhatsAppChatPanel = ({
  selectedContact = null,
  messages = [],
  onSendMessage,
  onContactSave,
  onAssignToMe,
  onUnassign,
  onAssignToAgent,
  currentAssignee,
  isLoading = false,
  sseConnectionStatus = 'disconnected',
  wabaId,
  conversationStatus = 'active',
  onConversationStatusChange,
}) => {
  const [messageText, setMessageText] = useState('');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalLoading, setContactModalLoading] = useState(false);
  const [contactModalError, setContactModalError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState(null);
  const [conversationActionLoading, setConversationActionLoading] = useState(false);
  const [showConversationPopup, setShowConversationPopup] = useState(false);
  const [isWindowExpired, setIsWindowExpired] = useState(false);

  // Check if 24-hour window is expired based on last message timestamp
  useEffect(() => {
    if (conversationStatus === 'closed' && messages.length > 0) {
      const lastInboundMessage = [...messages]
        .reverse()
        .find(msg => msg.direction === 'inbound');
      
      if (lastInboundMessage && lastInboundMessage.sent_at) {
        const lastMessageTime = new Date(lastInboundMessage.sent_at);
        const now = new Date();
        const hoursSinceLastMessage = (now - lastMessageTime) / (1000 * 60 * 60);
        
        setIsWindowExpired(hoursSinceLastMessage > 24);
      } else {
        setIsWindowExpired(true);
      }
    } else if (conversationStatus === 'active') {
      setIsWindowExpired(false);
    }
  }, [conversationStatus, messages]);

  // Ref for auto-scrolling to bottom
  const messagesEndRef = useRef(null);
  // Ref to track if user has manually scrolled up
  const shouldAutoScrollRef = useRef(true);

  // Get access token from Redux store
  const accessToken = useSelector((state) => state.auth.access_token?.token);

  // Load agents data on component mount or when accessToken changes
  useEffect(() => {
    if (accessToken) {
      loadAgentsData();
    }
  }, [accessToken]);

  // Auto-scroll to bottom when messages change (only if user is at bottom)
  useEffect(() => {
    if (messagesEndRef.current && shouldAutoScrollRef.current && messages.length > 0) {
      // Use a small delay to ensure DOM is updated after message rendering
      const timeoutId = setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [messages]);

  // Scroll to bottom when contact changes or component mounts with messages
  useEffect(() => {
    if (selectedContact && messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [selectedContact, messages.length]);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      shouldAutoScrollRef.current = true;
    }
  };

  // Handle scroll events to detect if user is at bottom
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Consider user at bottom if within 100px of bottom
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
    shouldAutoScrollRef.current = isAtBottom;
  };

  const loadAgentsData = async () => {
    setAgentsLoading(true);
    setAgentsError(null);

    try {
      const response = await whatsappAgentAPI.getManagerAgents(accessToken);

      if (response.success) {
        // Transform the data to match the expected format
        const transformedAgents = response.data.agents.map((agent) => ({
          id: agent.agent_id,
          name: agent.name,
          email: agent.email,
          lastActive: agent.last_active,
          role: agent.role,
          status: agent.status,
          assignedAt: agent.assigned_at,
        }));

        setAgents(transformedAgents);
      } else {
        setAgentsError(response.message || 'Failed to load agents data');
      }
    } catch (err) {
      console.error('Error loading agents:', err);
      setAgentsError('Failed to load agents data');
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (messageText.trim() && onSendMessage) {
      onSendMessage(messageText.trim());
      setMessageText('');
      // Scroll to bottom after sending message
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  const handleAddNote = async (noteData) => {
    const conversationId = messages.length > 0 ? messages[0].conversation_id : null;

    if (!conversationId) {
      console.error('No conversation ID available for note');
      return;
    }

    if (!accessToken) {
      console.error('No access token available');
      return;
    }

    try {
      const result = await whatsappAgentAPI.createNote(
        conversationId,
        noteData.noteText,
        noteData.agent,
        accessToken
      );

      if (result.success) {
        console.log('Note saved successfully');
      } else {
        console.error('API returned error:', result);
      }
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const handleAIAssist = async (aiData) => {
    const conversationId = messages.length > 0 ? messages[0].conversation_id : null;
    if (!conversationId) {
      console.error('No conversation ID available for AI assist');
      return;
    }

    try {
      const response = await whatsappAgentAPI.aiAssist(
        conversationId,
        aiData.text,
        aiData.tone,
        aiData.agent,
        accessToken
      );

      if (response.success && response.data) {
        // Set the AI-generated text back to the input field
        const generatedText = response.data.generated_text || response.data.aiGeneratedText;
        if (generatedText) {
          setMessageText(generatedText);
          console.log('AI assist successful: text transformed with tone', aiData.tone);
        } else {
          console.warn('AI assist response missing generated text');
        }
      } else {
        console.error('AI assist API returned error:', response.message);
      }
    } catch (error) {
      console.error('Error with AI assist:', error);
    }
  };

  const handleContactModalOpen = () => {
    setContactModalError(null);
    setIsContactModalOpen(true);
  };

  const handleContactModalClose = () => {
    setIsContactModalOpen(false);
    setContactModalError(null);
    setContactModalLoading(false);
  };

  const handleContactSave = async (contactData, contactId) => {
    setContactModalLoading(true);
    setContactModalError(null);

    try {
      if (onContactSave) {
        await onContactSave(contactData, contactId);
        handleContactModalClose();
      }
    } catch (error) {
      setContactModalError(error.message || 'Failed to save contact');
    } finally {
      setContactModalLoading(false);
    }
  };

  // Handle button click to show/hide popup
  const handleConversationButtonClick = () => {
    if (!messages.length || conversationActionLoading) return;

    if (conversationStatus === 'active') {
      // Show close conversation popup
      setShowConversationPopup(!showConversationPopup);
    } else {
      // Directly open conversation for closed state
      handleOpenConversation();
    }
  };

  // Handle close conversation action
  const handleCloseConversation = async () => {
    if (!messages.length || conversationActionLoading) return;

    const conversationId = messages[0].conversation_id;
    if (!conversationId || !accessToken) {
      console.error('[handleCloseConversation] Missing required parameters:', {
        conversationId: conversationId || 'undefined',
        hasAccessToken: !!accessToken,
      });
      return;
    }

    console.log('[handleCloseConversation] Initiating close for conversation:', conversationId);
    setConversationActionLoading(true);
    setShowConversationPopup(false);

    try {
      const result = await whatsappAgentAPI.closeConversation(conversationId, accessToken);

      console.log('[handleCloseConversation] API response:', {
        success: result.success,
        message: result.message,
        statusCode: result.status_code,
        hasData: !!result.data,
      });

      if (result.success) {
        console.log('[handleCloseConversation] Conversation closed successfully:', {
          conversationId,
          data: result.data,
        });

        setIsWindowExpired(false);

        if (onConversationStatusChange) {
          onConversationStatusChange(conversationId, 'closed', {
            ...result.data,
            windowExpired: false,
          });
        }
      } else {
        if (
          result.data?.alreadyClosed ||
          result.message?.toLowerCase().includes('already closed')
        ) {
          console.warn('[handleCloseConversation] Conversation is already closed:', {
            conversationId,
            message: result.message,
          });
          
          setIsWindowExpired(false);

          if (onConversationStatusChange) {
            onConversationStatusChange(conversationId, 'closed', {
              ...result.data,
              windowExpired: false,
            });
          }
        } else {
          console.error('[handleCloseConversation] Failed to close conversation:', {
            conversationId,
            message: result.message,
            statusCode: result.status_code,
            errorData: result.data,
          });
        }
      }
    } catch (error) {
      console.error('[handleCloseConversation] Exception occurred:', {
        errorName: error.name,
        errorMessage: error.message,
        stack: error.stack,
      });
    } finally {
      setConversationActionLoading(false);
      console.log(
        '[handleCloseConversation] Operation completed for conversation:',
        conversationId
      );
    }
  };

  // Handle open conversation action
  const handleOpenConversation = async () => {
    if (!messages.length || conversationActionLoading) return;

    const conversationId = messages[0].conversation_id;
    if (!conversationId || !accessToken) {
      console.error('Missing conversation ID or access token');
      return;
    }

    setConversationActionLoading(true);

    try {
      const result = await whatsappAgentAPI.openConversation(conversationId, accessToken);

      if (result.success) {
        console.log('Conversation opened successfully');

        // Clear window expiration flag on successful open
        setIsWindowExpired(false);

        // Notify parent component about status change
        if (onConversationStatusChange) {
          onConversationStatusChange(conversationId, 'active', {
            ...result.data,
            windowExpired: false,
          });
        }
      } else {
        console.error('Failed to open conversation:', result.message);

        // Handle specific error cases
        if (result.windowExpired) {
          console.warn('24-hour window expired, conversation cannot be reopened');

          // Set window expiration flag
          setIsWindowExpired(true);

          // Optionally notify parent to update UI
          if (onConversationStatusChange) {
            onConversationStatusChange(conversationId, 'closed', {
              ...result.data,
              windowExpired: true,
            });
          }
        }
      }
    } catch (error) {
      console.error('Error opening conversation:', error);
    } finally {
      setConversationActionLoading(false);
    }
  };

  // Get conversation status button properties
  const getConversationStatusButtonProps = () => {
    const isOpen = conversationStatus === 'active';

    console.log('Conversation status in chat panel:', conversationStatus, 'isOpen:', isOpen);

    return {
      title: isOpen ? 'Close conversation' : 'Open conversation',
      disabled: conversationActionLoading || !messages.length,
      loading: conversationActionLoading,
      isOpen,
    };
  };

  // Main render
  if (!selectedContact) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignSelf: 'stretch',
          gap: '1px',
          flex: '1',
          backgroundColor: '#FFFFFF',
          borderLeft: '1px solid #D0D5DD',
          borderRadius: '0px 20px 20px 0px',
          height: '100%',
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        <EmptyState />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignSelf: 'stretch',
        gap: '1px',
        flex: '1',
        backgroundColor: '#FFFFFF',
        borderLeft: '1px solid #D0D5DD',
        borderRadius: '0px 20px 20px 0px',
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
      }}
    >
      <ChatHeader
        contact={selectedContact}
        sseConnectionStatus={sseConnectionStatus}
        handleContactModalOpen={handleContactModalOpen}
        currentAssignee={currentAssignee}
        agents={agents}
        agentsLoading={agentsLoading}
        agentsError={agentsError}
        onAssignToMe={onAssignToMe}
        onUnassign={onUnassign}
        onAssignToAgent={onAssignToAgent}
        messages={messages}
        getConversationStatusButtonProps={getConversationStatusButtonProps}
        handleConversationButtonClick={handleConversationButtonClick}
        showConversationPopup={showConversationPopup}
        setShowConversationPopup={setShowConversationPopup}
        handleCloseConversation={handleCloseConversation}
        conversationActionLoading={conversationActionLoading}
      />
      <ChatMessages
        messages={messages}
        isLoading={isLoading}
        transformMessageForUI={transformMessageForUI}
        groupMessagesByDate={groupMessagesByDate}
        handleScroll={handleScroll}
        messagesEndRef={messagesEndRef}
      />
      <MessageInput
        value={messageText}
        onChange={(e) => setMessageText(e.target.value)}
        onSend={handleSendMessage}
        onAddNote={handleAddNote}
        onAIAssist={handleAIAssist}
        conversationId={messages.length > 0 ? messages[0].conversation_id : null}
        disabled={false}
        isLoading={isLoading}
        wabaId={wabaId}
        accessToken={accessToken}
        conversationStatus={conversationStatus}
        isWindowExpired={isWindowExpired}
      />

      {/* Contact Modal */}
      <AddEditContactModal
        isOpen={isContactModalOpen}
        onClose={handleContactModalClose}
        onSave={handleContactSave}
        contact={selectedContact}
        isLoading={contactModalLoading}
        error={contactModalError}
      />
    </div>
  );
};

WhatsAppChatPanel.propTypes = {
  selectedContact: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    phone: PropTypes.string,
    phoneNumber: PropTypes.string,
    avatar: PropTypes.string,
    isContact: PropTypes.bool,
    contactId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    contactData: PropTypes.shape({
      displayName: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      company: PropTypes.string,
      notes: PropTypes.string,
      tags: PropTypes.array,
    }),
  }),
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      conversation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      message_id: PropTypes.string,
      message_type: PropTypes.string,
      direction: PropTypes.oneOf(['inbound', 'outbound']).isRequired,
      text: PropTypes.string,
      content: PropTypes.shape({
        text: PropTypes.string,
        sender_name: PropTypes.string,
      }),
      sent_at: PropTypes.string.isRequired,
      status: PropTypes.string,
    })
  ),
  onSendMessage: PropTypes.func,
  onContactSave: PropTypes.func,
  onAssignToMe: PropTypes.func,
  onUnassign: PropTypes.func,
  onAssignToAgent: PropTypes.func,
  currentAssignee: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  sseConnectionStatus: PropTypes.oneOf(['connected', 'disconnected', 'error', 'failed']),
  wabaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  conversationStatus: PropTypes.oneOf(['active', 'closed']),
  onConversationStatusChange: PropTypes.func,
};

export default WhatsAppChatPanel;
